import * as debug from './debug.service';
import { Injectable } from '@angular/core';
import { appConfig, environment } from 'src/environments/environment';
import { HttpErrorResponse } from '@angular/common/http';
import { LogsBulkInsertResponse } from '../responses/logs-bulk-insert-response';
import { HttpService } from './http.service';
import { LogsCreateRequest } from '../requests/logs-create-request';
import { ErrorDbService } from './error-db.service';
import { parseErrorMessage, isEmptyObject, delay, getCurrentWindowUrl } from '../utils/utility.common';
import { ProfileDbService } from './profile-db.service';
import { catchError } from 'rxjs/operators';
import { MenuService } from './menu.service';

@Injectable({ providedIn: 'root' })
export class LoggingService {
	private isRetryRunning = false;

	constructor(protected readonly menuService: MenuService
		, protected readonly errorDbService: ErrorDbService
		, protected readonly profileDbService: ProfileDbService
		, private readonly httpService: HttpService
	) {

		debug.log('004 LoggingService instantiated');

		this.sendStoredErrors(`LoggingService constructor`);
	}

	async logError(error: Error | HttpErrorResponse, caller: string) {
		// tslint:disable-next-line: no-string-literal
		const connection = navigator['connection'];
		const downlink = connection ? connection.downlink : undefined;
		const effectiveType = connection ? connection.effectiveType : undefined;
		const userAgent = {
			language: navigator.language,
			platform: navigator.platform,
			agent: navigator.userAgent,
			connectionDownlink: downlink,
			connectionEffectiveType: effectiveType
		};

		let stackTrace: string;

		if (error instanceof HttpErrorResponse) {
			stackTrace = JSON.stringify(error);
		} else {
			stackTrace = error.stack;
		}

		const message = parseErrorMessage(error);
		const currentWindowUrl = getCurrentWindowUrl();
		const email = await this.profileDbService.getEmail();
		const body = new LogsCreateRequest();

		body.message = message;
		body.stackTrace = stackTrace;
		body.logLevel = 'Error';
		body.additionalInfo = `Environment: ${environment.name} ${JSON.stringify(userAgent)}`;
		body.callSite = `${email}|${currentWindowUrl}`; // TODO: remove email
		body.clientTimeStamp = new Date().toLocaleString();
		body.source = `${appConfig.logSource}`;
		body.releaseVersion = `${environment.appVersion}`;

		const wasOk = await this.sendError(body, caller);

		if (isEmptyObject(wasOk)) {
			const isTable = error.name === 'Forced logout';
			if (isTable) {
				debug.log(`${caller}->LoggingService.logError unable to send to server. Saving to local storage:`, [error]);
			} else {
				debug.log(`${caller}->LoggingService.logError unable to send to server. Saving to local storage:`, error);
			}

			try {
				await this.errorDbService.store(JSON.stringify(body));
			} catch (ex) {
				debug.warn(`${caller}->LoggingService.logError unable to save to local storage.. exception: `, ex);
			}
		} else {
			debug.log(`${caller}->LoggingService.logError. Error has been saved to server:`, error);
		}

		return Promise.resolve();
	}

	async sendStoredErrors(caller: string) {
		if (this.isRetryRunning) {
			return Promise.resolve();
		}

		let attempts = 1;

		const retry = async () => {
			const errors = await this.errorDbService.getAll();
			if (errors.length === 0) {
				debug.log(`${caller}->LoggingService.sendStoredErrors. No more errors to send.. exiting..`);
				return Promise.resolve();
			}

			const storedErrors = errors.map(error => {
				const item: LogsCreateRequest = JSON.parse(error.error);
				item.type = 'LocalStorage';
				// tslint:disable-next-line: no-string-literal
				item.id = error['id'];
				return item;
			});

			const response = await this.sendError(storedErrors, caller);

			if (!response) {

				this.isRetryRunning = true;

				if (attempts < 4) {
					attempts++;
					debug.log(`${caller}->LoggingService.sendStoredErrors Retrying.. ${attempts - 1} attempts.`);

					await delay(attempts * 60_000);

					return await retry();
					// // setTimeout(retry, attempts * 60_000); 
				} else {
					debug.log(`${caller}->LoggingService.sendStoredErrors ${attempts} retries exhausted. Will try again later.`);

					return Promise.resolve();
				}
			} else {

				let idsToDelete: number[];

				if (response.hasError) {
					idsToDelete = response.createdItems;
				} else {
					idsToDelete = errors.map(error => error.id);
				}

				debug.log(`${caller}->LoggingService.sendStoredErrors bulkDelete.. ${JSON.stringify(idsToDelete)}`);

				await this.errorDbService.bulkDelete(idsToDelete);

				this.isRetryRunning = false;

				return Promise.resolve();
			}
		};

		return await retry();
	}

	private async sendError(errors: LogsCreateRequest[] | LogsCreateRequest, caller: string): Promise<LogsBulkInsertResponse> {

		if (navigator.onLine) {

			const isLoggedIn = MenuService.isLoggedIn;

			if (!isLoggedIn) {
				debug.log(`${caller}->LoggingService.sendError App is not logged-in. Using local storage:`);
				return Promise.resolve(new LogsBulkInsertResponse());
			}

			const token = MenuService.accessToken; // await this.securityService.getAccessToken();

			if (!token) {
				debug.log(`${caller}->LoggingService.sendError token is null.`);
				return Promise.resolve(new LogsBulkInsertResponse());
			}

			let body: LogsCreateRequest[];

			if (!Array.isArray(errors)) {
				body = [errors];
			} else {
				body = errors;
			}

			return this.httpService.post<LogsBulkInsertResponse>(appConfig.logger, body)
				.pipe(
					catchError(ex => {
						return Promise.resolve(new LogsBulkInsertResponse());
					})
				)
				.toPromise();

		} else {
			debug.log(`${caller}->LoggingService.sendError navigator.onLine = false`);
			return Promise.resolve(new LogsBulkInsertResponse());
		}
	}
}
